import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useRealtimeSubscription from '../../../hooks/useRealtimeSubscription';

const DATE_RANGES = [
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y', days: 365 },
];

const ROIByCampaignPanel = ({ brandId }) => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(90);
  const [roiData, setRoiData] = useState([]);
  const [bestROI, setBestROI] = useState(null);

  const loadROI = useCallback(async () => {
    if (!brandId) return;
    try {
      const since = new Date();
      since?.setDate(since?.getDate() - dateRange);

      // Get campaigns
      const { data: campaigns, error: campErr } = await supabase
        ?.from('campaigns')
        ?.select('id, title, budget, spent, status, reward_amount')
        ?.eq('brand_id', brandId)
        ?.in('status', ['active', 'completed', 'paused'])
        ?.gte('created_at', since?.toISOString());

      if (campErr || !campaigns?.length) {
        setRoiData([]);
        setLoading(false);
        return;
      }

      const campaignIds = campaigns?.map(c => c?.id);

      // Get transactions per campaign
      const { data: txData } = await supabase
        ?.from('transactions')
        ?.select('campaign_id, amount, purchase_value, status')
        ?.in('campaign_id', campaignIds);

      const txMap = {};
      txData?.forEach(t => {
        if (!txMap?.[t?.campaign_id]) txMap[t.campaign_id] = { cashbackEarned: 0, conversions: 0, revenue: 0 };
        const cashback = parseFloat(t?.amount) || 0;
        const revenue = parseFloat(t?.purchase_value) || 0;
        if (t?.status === 'approved') {
          txMap[t.campaign_id].cashbackEarned += cashback;
          txMap[t.campaign_id].conversions += 1;
          txMap[t.campaign_id].revenue += revenue;
        }
      });

      const mapped = campaigns?.map(c => {
        const spend = parseFloat(c?.spent) || parseFloat(c?.budget) || 0;
        const tx = txMap?.[c?.id] || { cashbackEarned: 0, conversions: 0, revenue: 0 };
        const roi = spend > 0 ? (((tx?.revenue - spend) / spend) * 100) : 0;
        return {
          name: c?.title?.length > 16 ? c?.title?.substring(0, 16) + '…' : c?.title,
          fullName: c?.title,
          spend: parseFloat(spend?.toFixed(2)),
          cashback: parseFloat(tx?.cashbackEarned?.toFixed(2)),
          revenue: parseFloat(tx?.revenue?.toFixed(2)),
          conversions: tx?.conversions,
          roi: parseFloat(roi?.toFixed(1)),
          status: c?.status,
        };
      })?.sort((a, b) => b?.roi - a?.roi);

      setRoiData(mapped);
      setBestROI(mapped?.[0] || null);
    } catch (err) {
      console.error('[ROIByCampaignPanel] error:', err);
    } finally {
      setLoading(false);
    }
  }, [brandId, dateRange]);

  useEffect(() => {
    setLoading(true);
    loadROI();
  }, [loadROI]);

  useRealtimeSubscription({
    channelName: `brand-roi-${brandId}`,
    table: 'transactions',
    filter: brandId ? `brand_id=eq.${brandId}` : undefined,
    enabled: !!brandId,
    onChange: () => loadROI(),
  });

  useRealtimeSubscription({
    channelName: `brand-roi-campaigns-${brandId}`,
    table: 'campaigns',
    filter: brandId ? `brand_id=eq.${brandId}` : undefined,
    enabled: !!brandId,
    onChange: () => loadROI(),
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const d = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg min-w-[180px]">
          <p className="font-semibold text-foreground text-sm mb-2">{d?.fullName}</p>
          {payload?.map(p => (
            <div key={p?.dataKey} className="flex items-center justify-between gap-4 text-xs">
              <span style={{ color: p?.fill || p?.stroke }} className="capitalize">{p?.name}</span>
              <span className="font-medium text-foreground">
                {p?.dataKey === 'roi' ? `${p?.value}%` : `$${p?.value}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">ROI by Campaign</h3>
            <p className="text-xs text-muted-foreground">Spend vs cashback vs revenue returns</p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {DATE_RANGES?.map(r => (
            <button
              key={r?.days}
              onClick={() => setDateRange(r?.days)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                dateRange === r?.days
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              {r?.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : roiData?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="BarChart2" size={40} className="text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground text-sm">No campaign data in this period</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Total Spend</p>
              <p className="text-lg font-bold text-foreground">
                ${roiData?.reduce((s, d) => s + d?.spend, 0)?.toFixed(0)}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Total Revenue</p>
              <p className="text-lg font-bold text-emerald-600">
                ${roiData?.reduce((s, d) => s + d?.revenue, 0)?.toFixed(0)}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Avg ROI</p>
              <p className={`text-lg font-bold ${
                roiData?.reduce((s, d) => s + d?.roi, 0) / roiData?.length >= 0
                  ? 'text-emerald-600' : 'text-red-500'
              }`}>
                {(roiData?.reduce((s, d) => s + d?.roi, 0) / roiData?.length)?.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Bar Chart */}
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={roiData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => `$${v}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  formatter={v => <span style={{ color: 'var(--muted-foreground)' }}>{v}</span>}
                />
                <Bar dataKey="spend" name="Spend" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="cashback" name="Cashback" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* ROI per campaign list */}
          <div className="mt-4 space-y-2">
            {roiData?.map(c => (
              <div key={c?.name} className="flex items-center justify-between py-2 border-t border-border/50">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    c?.roi >= 100 ? 'bg-emerald-500' : c?.roi >= 0 ? 'bg-amber-500' : 'bg-red-500'
                  }`} />
                  <span className="text-sm text-foreground truncate">{c?.fullName}</span>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <span className="text-xs text-muted-foreground">{c?.conversions} conv.</span>
                  <span className={`text-sm font-semibold ${
                    c?.roi >= 0 ? 'text-emerald-600' : 'text-red-500'
                  }`}>{c?.roi >= 0 ? '+' : ''}{c?.roi}% ROI</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ROIByCampaignPanel;
